import { useCallback, useEffect, useRef, useState } from 'react';
import { callBackend } from '../../../utils/api';
import { loadedFactorSlice } from '../../../utils/loadedFactorSlice';
import {
    buildLightweightFactorContext,
    describePosSizingSavedFactorDataSync,
    getSavedDraftReadinessBlocker,
    isExpectedLegacySnapshotSyncError,
    isExpectedSavedDraftPrerequisiteError,
    POS_SIZING_SAVED_FACTOR_DATA_BLOCKER_CODE,
} from '../dashboardUtils';
import { isValidPosSizingPublicationBase } from '../../../utils/posSizingPublicationContract';
import {
    POSITION_SIZING_WORKSPACE_HYDRATION,
    POSITION_SIZING_WORKSPACE_REQUEST,
    requirePositionSizingWorkspaceHydration,
} from '../../../utils/positionSizingAttributeContract';

const CLOUD_RUN_ERROR_PATTERN = /Cloud Run call failed:\s*(\d+)\s*-\s*(.*)$/;
const SAVED_DRAFT_HYDRATION_CONTRACT = POSITION_SIZING_WORKSPACE_HYDRATION;
const SAVED_CONTEXT_STATUS_CONTRACT = 'pos-sizing-saved-context-status.v1';
const SHA256_PATTERN = /^[0-9a-f]{64}$/;
const LEGACY_IDENTITY_CODES = new Set(['ambiguous_identifier', 'unresolved_identifier']);
const SAVED_DRAFT_SYNC_REQUIRED_CODES = new Set([
    'no_saved_draft',
    'position_sizing_durable_context_unknown_legacy',
    POS_SIZING_SAVED_FACTOR_DATA_BLOCKER_CODE,
]);
const SUCCESS_STATUS = 'SUCCESS';
const IDENTITY_PARTIAL_STATUS = 'IDENTITY_PARTIAL';
const IDENTITY_UNRESOLVED_ALL_STATUS = 'IDENTITY_UNRESOLVED_ALL';

const normalizeText = (value) => (
    value == null ? '' : String(value).trim()
);

const isRecord = (value) => Boolean(
    value && typeof value === 'object' && !Array.isArray(value)
);

const normalizeCount = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return null;
    }
    return parsed;
};

const countActionableOutboundAmbiguities = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return 0;
    }

    const explicitCount = normalizeCount(payload?.actionable_error_count);
    if (explicitCount != null) {
        return explicitCount;
    }

    const errors = Array.isArray(payload?.errors) ? payload.errors : [];
    return errors.filter((item) => {
        const code = normalizeText(item?.code).toLowerCase();
        if (code !== 'ambiguous_identifier') {
            return false;
        }
        const candidates = Array.isArray(item?.candidates) ? item.candidates : [];
        return candidates.some((candidate) => normalizeText(candidate?.barrid));
    }).length;
};

const parseCloudRunError = (error) => {
    if (error?.name === 'BackendHttpError') {
        return { code: normalizeText(error.code).toLowerCase(), detail: error.detail };
    }
    const rawMessage = error instanceof Error ? error.message : normalizeText(error);
    const match = rawMessage.match(CLOUD_RUN_ERROR_PATTERN);
    if (!match) {
        return {
            code: '',
            detail: null,
        };
    }

    const payloadText = normalizeText(match[2]);

    let parsedPayload = null;
    try {
        parsedPayload = payloadText ? JSON.parse(payloadText) : null;
    } catch {
        parsedPayload = null;
    }

    const detail = parsedPayload && typeof parsedPayload === 'object' && 'detail' in parsedPayload
        ? parsedPayload.detail
        : parsedPayload;
    const normalizedDetail = detail && typeof detail === 'object' ? detail : null;

    return {
        code: normalizeText(normalizedDetail?.code).toLowerCase(),
        detail: normalizedDetail,
    };
};

const normalizeStartupIdentityStatus = ({
    status = '',
    success = null,
    identityResolution = null,
    hasResolvedRows = false,
} = {}) => {
    const normalizedStatus = normalizeText(status).toUpperCase();
    if (
        normalizedStatus === SUCCESS_STATUS
        || normalizedStatus === IDENTITY_PARTIAL_STATUS
        || normalizedStatus === IDENTITY_UNRESOLVED_ALL_STATUS
    ) {
        return normalizedStatus;
    }
    if (normalizedStatus) {
        return normalizedStatus;
    }

    const identityCode = normalizeText(identityResolution?.code).toLowerCase();
    if (identityCode === 'identity_partial') {
        return IDENTITY_PARTIAL_STATUS;
    }
    if (identityCode === 'identity_unresolved_all') {
        return IDENTITY_UNRESOLVED_ALL_STATUS;
    }
    if (LEGACY_IDENTITY_CODES.has(identityCode)) {
        return hasResolvedRows ? IDENTITY_PARTIAL_STATUS : IDENTITY_UNRESOLVED_ALL_STATUS;
    }

    if (success === false) {
        return hasResolvedRows ? IDENTITY_PARTIAL_STATUS : IDENTITY_UNRESOLVED_ALL_STATUS;
    }
    if (success === true) {
        return hasResolvedRows ? SUCCESS_STATUS : IDENTITY_UNRESOLVED_ALL_STATUS;
    }
    return hasResolvedRows ? SUCCESS_STATUS : IDENTITY_UNRESOLVED_ALL_STATUS;
};

const buildNonFatalIdentityMessage = ({
    status = '',
    responseMessage = '',
} = {}) => {
    if (status === IDENTITY_UNRESOLVED_ALL_STATUS) {
        return (
            responseMessage
            || 'No securities resolved into pos-sizing identity scope. Review MSI drift choices and retry.'
        );
    }
    return (
        responseMessage
        || 'Some securities could not be identity-resolved for pos sizing; showing resolved rows only.'
    );
};

const isCompleteRunnableHydration = (result) => {
    const syncStatus = result?.pos_sizing_sync_status;
    const readiness = syncStatus?.saved_draft_readiness;
    const optimizerContext = syncStatus?.optimizer_context;
    const readinessSha = readiness?.context_document_sha256;
    const optimizerSha = optimizerContext?.context_document_sha256;
    return Boolean(
        result?.hydration_contract === SAVED_DRAFT_HYDRATION_CONTRACT
        && isRecord(syncStatus)
        && syncStatus?.success === true
        && isRecord(readiness)
        && readiness?.can_load_saved_draft === true
        && readiness?.blocker == null
        && readiness?.verification?.status !== 'unavailable'
        && SHA256_PATTERN.test(readinessSha ?? '')
        && isRecord(optimizerContext)
        && optimizerContext?.contract === SAVED_CONTEXT_STATUS_CONTRACT
        && ['current', 'stale', 'unknown'].includes(optimizerContext?.freshness)
        && optimizerContext?.availability === 'available'
        && optimizerContext?.runnability === 'runnable'
        && optimizerContext?.blocker == null
        && optimizerSha === readinessSha
        && Array.isArray(optimizerContext?.optimizer_rows)
        && Array.isArray(result?.pos_sizing)
        && isValidPosSizingPublicationBase(result?.publication_base)
    );
};

const getCompleteBlockedHydrationStatus = (detail) => {
    const syncStatus = detail?.pos_sizing_sync_status;
    const readiness = syncStatus?.saved_draft_readiness;
    const optimizerContext = syncStatus?.optimizer_context;
    const readinessBlocker = readiness?.blocker;
    const optimizerBlocker = optimizerContext?.blocker;
    const code = normalizeText(readinessBlocker?.code);
    const action = normalizeText(readinessBlocker?.action);
    const message = normalizeText(readinessBlocker?.message);
    if (
        detail?.hydration_contract !== SAVED_DRAFT_HYDRATION_CONTRACT
        || !isRecord(syncStatus)
        || syncStatus?.success !== true
        || !isRecord(readiness)
        || readiness?.can_load_saved_draft !== false
        || !isRecord(readinessBlocker)
        || !code
        || !action
        || !message
        || !isRecord(optimizerContext)
        || optimizerContext?.contract !== SAVED_CONTEXT_STATUS_CONTRACT
        || !['current', 'stale', 'unknown'].includes(optimizerContext?.freshness)
        || optimizerContext?.availability !== 'unavailable'
        || optimizerContext?.runnability !== 'blocked'
        || !Array.isArray(optimizerContext?.optimizer_rows)
        || !isRecord(optimizerBlocker)
        || normalizeText(optimizerBlocker?.code) !== code
        || normalizeText(optimizerBlocker?.action) !== action
    ) {
        return null;
    }
    return syncStatus;
};

export default function usePosSizingDataFetcher({
    userUid,
    factorSliceScopeKey = null,
    exclusiveCoreLoadOwnerRef = null,
    selectedFactorModel,
    factorSnapshotDate,
    factorDataModel,
    posSizingIdioVolSource,
    selectedBetaSource,
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
    setError,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen,
    setPosSizingData,
    setPosSizingLoading,
    setPosSizingLoaded,
    posSizingRequestIdRef,
    persistPosSizingPortfolioRef,
    posSizingPublicationBaseRef,
    setPosSizingSyncStatus,
    setPosSizingAttributeWorkspaceHydration,
    onSavedDraftSyncRequired,
    onWatchlistStartAvailable,
}) {
    // Persistence can invalidate the shared data request ID without starting a
    // replacement hydration. Loading belongs to this invocation, not that ID.
    const scopeKey = factorSliceScopeKey ?? userUid;
    const loadingScopeRef = useRef({ userUid, scopeKey, active: true });
    const loadingOwnerRef = useRef(null);
    if (loadingScopeRef.current.userUid !== userUid
        || loadingScopeRef.current.scopeKey !== scopeKey) {
        loadingScopeRef.current = { userUid, scopeKey, active: true };
    }
    const loadingScope = loadingScopeRef.current;
    useEffect(() => {
        loadingScope.active = true;
        if (loadingOwnerRef.current && loadingOwnerRef.current.scope !== loadingScope) {
            // Release only the departed scope's spinner. A new scope's layout
            // hydration may already have acquired its own owner.
            loadingOwnerRef.current = null;
            setPosSizingLoading(false);
        }
        return () => { loadingScope.active = false; };
    }, [loadingScope, setPosSizingLoading]);

    const [savedDraftLoadError, setSavedDraftLoadError] = useState(null);
    const [posSizingOutboundResolutionDialogOpen, setPosSizingOutboundResolutionDialogOpen] = useState(false);
    const [posSizingOutboundResolutionIdentityResolution, setPosSizingOutboundResolutionIdentityResolution] = useState(null);

    const syncPosSizingOutboundResolutionDialog = useCallback((identityResolution) => {
        const actionableCount = countActionableOutboundAmbiguities(identityResolution);
        if (actionableCount > 0) {
            setPosSizingOutboundResolutionIdentityResolution(identityResolution);
            setPosSizingOutboundResolutionDialogOpen(true);
            return;
        }
        setPosSizingOutboundResolutionDialogOpen(false);
        setPosSizingOutboundResolutionIdentityResolution(null);
    }, []);

    const fetchPosSizingData = useCallback(async (options = {}) => {
        const isCurrentScope = () => loadingScope.active && loadingScopeRef.current === loadingScope;
        const stagePublicationState = options?.stagePublicationState;
        const staging = typeof stagePublicationState === 'function';
        const ownsPublicationRead = () => (
            (!exclusiveCoreLoadOwnerRef?.current || (staging && typeof options.isCurrentRequest === 'function'))
            && (typeof options.isCurrentRequest !== 'function' || options.isCurrentRequest())
        );
        if (!userUid || !isCurrentScope() || !ownsPublicationRead()) return null;
        const requestId = options?.requestId ?? (posSizingRequestIdRef.current + 1);
        posSizingRequestIdRef.current = requestId;
        const isCurrent = () => isCurrentScope() && posSizingRequestIdRef.current === requestId
            && ownsPublicationRead();
        if (staging && options?.persistPortfolio) {
            throw new Error('Prepared Position Sizing hydration cannot save a portfolio.');
        }
        const publish = (apply) => {
            if (staging) stagePublicationState(apply, isCurrent);
            else apply();
        };
        const preparedStartingValues = options?.preparedTarget?.actualStartingValues?.values;
        const idioVolSource = options?.idioVolSource ?? posSizingIdioVolSource;
        const factorContext = buildLightweightFactorContext({
            factorModel: selectedFactorModel,
            factorDataModel,
            factorSnapshotDate,
            betaSource: selectedBetaSource,
            idioVolSource,
            override: options?.matricesOverride ?? null,
        });
        const snapshotDate = factorContext.factorSnapshotDate;
        const resolvedFactorDataModel = factorContext.factorDataModel;
        const loadingOwner = { scope: loadingScope };
        loadingOwnerRef.current = loadingOwner;
        if (!staging) setPosSizingLoading(true);
        let didPersistPortfolio = options?.persistPortfolio ? false : true;
        const requestBasePayload = {
            contract: POSITION_SIZING_WORKSPACE_REQUEST,
            // Keep every strict workspace-contract field present during cold
            // start, before the current factor context has hydrated.
            factor_model: factorContext.factorModel ?? null,
            default_target_vol_pct: preparedStartingValues?.target_vol_pct ?? defaultTargetVolPct,
            default_target_vol_dollar: preparedStartingValues?.target_vol_dollar ?? defaultTargetVolDollar,
            default_scaler: preparedStartingValues?.scaler ?? defaultScaler,
            idio_vol_source: factorContext.idioVolSource,
            beta_source: factorContext.betaSource ?? null,
            factor_snapshot_date: snapshotDate ?? null,
            factor_data_model: resolvedFactorDataModel ?? null,
        };
        const requestPosSizingCalculations = () => (
            callBackend('get_pos_sizing_workspace_v2', 'POST', {
                ...requestBasePayload,
            })
        );
        const clearSavedDraftView = () => {
            posSizingPublicationBaseRef.current = null;
            publish(() => {
                setPosSizingAttributeWorkspaceHydration?.(null);
                setPosSizingData([]);
                syncPosSizingOutboundResolutionDialog(null);
            });
        };
        const blockSavedDraftLoad = (
            message,
            severity = 'warning',
            { syncStatus = null, syncPrompt = null } = {},
        ) => {
            clearSavedDraftView();
            publish(() => {
                setSavedDraftLoadError({ userUid, message, syncPrompt });
                setPosSizingSyncStatus?.(syncStatus);
                setError(null);
                setSnackbarMessage(message);
                setSnackbarSeverity(severity);
                setSnackbarOpen(true);
            });
            return null;
        };
        try {
            const result = requirePositionSizingWorkspaceHydration(
                await requestPosSizingCalculations(),
            );

            if (!isCurrent()) {
                return null;
            }

            if (!isCompleteRunnableHydration(result)) {
                return blockSavedDraftLoad(
                    'Unable to verify the saved Position Sizing context. Sync Draft to continue.',
                    'error',
                );
            }
            const syncStatus = result.pos_sizing_sync_status;
            publish(() => {
                setSavedDraftLoadError(null);
                setPosSizingSyncStatus?.(syncStatus);
            });
            posSizingPublicationBaseRef.current = result.publication_base;
            publish(() => setPosSizingAttributeWorkspaceHydration?.({
                key: `${requestId}:${result.publication_base.portfolio_row_version}`,
                factorSliceUserUid: userUid,
                factorSliceScopeKey: options?.preparedTarget?.factorSliceScopeKey ?? factorSliceScopeKey ?? userUid,
                factorSliceAccepted: true,
                factorSlice: loadedFactorSlice(syncStatus.optimizer_context.saved_context),
                capability: result.attribute_capability,
                state: result.attribute_state,
                calculation: result.attribute_calculation,
            }));
            const posSizingRows = result.pos_sizing;
            const normalizedStatus = normalizeStartupIdentityStatus({
                status: result.status,
                success: result.success,
                identityResolution: result.identity_resolution,
                hasResolvedRows: posSizingRows.length > 0,
            });
            const isIdentityNonFatal = (
                normalizedStatus === IDENTITY_PARTIAL_STATUS
                || normalizedStatus === IDENTITY_UNRESOLVED_ALL_STATUS
            );
            publish(() => {
                setError(null);
                setPosSizingData(posSizingRows);
                if (isIdentityNonFatal) {
                    setSnackbarMessage(
                        buildNonFatalIdentityMessage({
                            status: normalizedStatus,
                            responseMessage: result.message,
                        }),
                    );
                    setSnackbarSeverity('warning');
                    setSnackbarOpen(true);
                }
                syncPosSizingOutboundResolutionDialog(result.identity_resolution);
            });
            if (options?.persistPortfolio && posSizingRows.length > 0) {
                didPersistPortfolio = await persistPosSizingPortfolioRef.current?.(
                    posSizingRows,
                );
            }
            return { ...result, _persisted: didPersistPortfolio, _workspace_verified: true };
        } catch (err) {
            if (!isCurrent()) {
                return null;
            }
            const parsedCloudRunError = parseCloudRunError(err);
            const normalizedErrorText = normalizeText(err?.message).toLowerCase();
            // The page keeps the prompt so its error banner can reopen Sync Draft.
            const promptSavedDraftSync = (message) => {
                const prompt = { code: parsedCloudRunError.code, message };
                publish(() => onSavedDraftSyncRequired?.(prompt));
                return prompt;
            };
            const claimsHydrationContract = Object.prototype.hasOwnProperty.call(
                parsedCloudRunError.detail || {},
                'hydration_contract',
            );
            const hydrationStatus = getCompleteBlockedHydrationStatus(
                parsedCloudRunError.detail,
            );
            if (claimsHydrationContract && !hydrationStatus) {
                return blockSavedDraftLoad(
                    'Unable to verify the saved Position Sizing context. Sync Draft to continue.',
                    'error',
                );
            }
            if (hydrationStatus) {
                const hydrationBlocker = getSavedDraftReadinessBlocker(hydrationStatus)
                    || hydrationStatus?.optimizer_context?.blocker
                    || parsedCloudRunError.detail;
                const startBase = parsedCloudRunError.detail?.publication_base;
                if (
                    normalizeText(hydrationBlocker?.code).toLowerCase() === 'no_saved_draft'
                    && parsedCloudRunError.detail?.can_start_from_watchlist === true
                    && isValidPosSizingPublicationBase(startBase)
                    && startBase.portfolio_id === null
                ) {
                    // A Watchlist-only user starts with an empty, editable
                    // table; the first save creates the draft from its rows.
                    clearSavedDraftView();
                    posSizingPublicationBaseRef.current = startBase;
                    publish(() => {
                        setPosSizingSyncStatus?.(hydrationStatus);
                        setSavedDraftLoadError(null);
                        setError(null);
                        // A member without starting values chooses them first.
                        onWatchlistStartAvailable?.();
                    });
                    return null;
                }
                const hydrationBlockerCode = normalizeText(hydrationBlocker?.code).toLowerCase();
                const hydrationBlockerMessage = (
                    hydrationBlockerCode === POS_SIZING_SAVED_FACTOR_DATA_BLOCKER_CODE
                        ? describePosSizingSavedFactorDataSync(hydrationStatus)
                        : hydrationBlocker?.message
                ) || 'Unable to verify the saved Position Sizing context. Sync Draft to continue.';
                return blockSavedDraftLoad(
                    hydrationBlockerMessage,
                    hydrationBlocker?.code === 'readiness_verification_unavailable'
                        ? 'error'
                        : 'warning',
                    {
                        syncStatus: hydrationStatus,
                        syncPrompt: SAVED_DRAFT_SYNC_REQUIRED_CODES.has(hydrationBlockerCode)
                            ? promptSavedDraftSync(hydrationBlockerMessage)
                            : null,
                    },
                );
            }
            if (
                parsedCloudRunError.code === 'no_saved_draft'
                || normalizedErrorText.includes('no saved pos-sizing draft')
            ) {
                const message = 'No saved pos-sizing draft was found. Sync Draft to create one.';
                return blockSavedDraftLoad(
                    message,
                    'warning',
                    { syncPrompt: promptSavedDraftSync(message) },
                );
            }
            if (isExpectedLegacySnapshotSyncError(err)) {
                const message = 'This pos-sizing draft predates durable context evidence. Sync Draft to continue.';
                return blockSavedDraftLoad(
                    message,
                    'warning',
                    { syncPrompt: promptSavedDraftSync(message) },
                );
            }
            if (isExpectedSavedDraftPrerequisiteError(err)) {
                const message = 'Saved pos-sizing draft references stale factor prerequisites. Sync Draft to continue.';
                return blockSavedDraftLoad(
                    message,
                    'warning',
                    {
                        syncPrompt: SAVED_DRAFT_SYNC_REQUIRED_CODES.has(parsedCloudRunError.code)
                            ? promptSavedDraftSync(message)
                            : null,
                    },
                );
            }
            console.error('Error fetching pos sizing data:', err);
            return blockSavedDraftLoad(
                'Unable to retrieve or verify the saved Position Sizing context. Sync Draft to continue.',
                'error',
            );
        } finally {
            if (isCurrent()) {
                publish(() => setPosSizingLoaded(true));
            }
            if (isCurrentScope() && loadingOwnerRef.current === loadingOwner) {
                loadingOwnerRef.current = null;
                if (staging) publish(() => setPosSizingLoading(false));
                else setPosSizingLoading(false);
            }
        }
    }, [
        userUid,
        loadingScope,
        exclusiveCoreLoadOwnerRef,
        factorSliceScopeKey,
        selectedFactorModel,
        factorSnapshotDate,
        factorDataModel,
        posSizingIdioVolSource,
        persistPosSizingPortfolioRef,
        posSizingPublicationBaseRef,
        posSizingRequestIdRef,
        selectedBetaSource,
        defaultTargetVolPct,
        defaultTargetVolDollar,
        defaultScaler,
        setError,
        setPosSizingData,
        setPosSizingAttributeWorkspaceHydration,
        setPosSizingLoaded,
        setPosSizingLoading,
        setPosSizingSyncStatus,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
        onSavedDraftSyncRequired,
        onWatchlistStartAvailable,
        syncPosSizingOutboundResolutionDialog,
    ]);

    const closePosSizingOutboundResolutionDialog = useCallback(() => {
        setPosSizingOutboundResolutionDialogOpen(false);
    }, []);

    const retryPosSizingAfterOutboundChoice = useCallback(async () => {
        setPosSizingOutboundResolutionDialogOpen(false);
        setPosSizingOutboundResolutionIdentityResolution(null);
        return await fetchPosSizingData();
    }, [fetchPosSizingData]);

    const visibleSavedDraftLoadError = savedDraftLoadError?.userUid === userUid
        ? savedDraftLoadError : null;
    return {
        fetchPosSizingData,
        posSizingLoadError: visibleSavedDraftLoadError?.message ?? null,
        posSizingLoadSyncPrompt: visibleSavedDraftLoadError?.syncPrompt ?? null,
        posSizingOutboundResolutionDialogOpen,
        posSizingOutboundResolutionIdentityResolution,
        closePosSizingOutboundResolutionDialog,
        retryPosSizingAfterOutboundChoice,
    };
}
